import { useState } from 'react';
import PageMeta from '@/components/base/PageMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo, brandStory, brandValues, faqs } from '@/mocks/siteInfo';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pn291n2trhb35gvi9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMsg('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMsg(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 爱情岛 | 让每一段爱情，都拥有属于自己的岛屿"
        description={`关于爱情岛：${brandStory.paragraphs[0].slice(0, 100)}。了解我们的品牌故事、核心价值观和联系方式。`}
        keywords="爱情岛关于我们,爱情岛品牌故事,情侣旅行平台"
        canonicalUrl={`${siteInfo.url}/about`}
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }} />

      <SchemaOrg type="Organization" data={{
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        foundingDate: `${siteInfo.foundingYear}`,
        contactPoint: { '@type': 'ContactPoint', telephone: siteInfo.phone, contactType: 'customer service', email: siteInfo.email },
        address: { '@type': 'PostalAddress', streetAddress: siteInfo.address },
        sameAs: [siteInfo.socialLinks.weibo, siteInfo.socialLinks.xiaohongshu, siteInfo.socialLinks.douyin],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: `关于我们 - ${siteInfo.name}`,
        url: `${siteInfo.url}/about`,
        isPartOf: { '@type': 'WebSite', name: siteInfo.name, url: siteInfo.url },
      }} />

      <SchemaOrg type="FAQPage" data={{
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      }} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-20 md:pt-24 pb-10 md:pb-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

              <div className="max-w-3xl">
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-900 mb-2">
                  {brandStory.title}
                </h1>
                <p className="text-lg md:text-xl text-foreground-500 font-heading">
                  {brandStory.subtitle}
                </p>
              </div>
            </div>
          </div>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  {brandStory.paragraphs.map((p, i) => (
                    <p key={i} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
                      {p}
                    </p>
                  ))}
                </div>
                <div className="relative h-72 md:h-96 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Romantic%20travel%20magazine%20editorial%20office%20with%20world%20map%20on%20wall%2C%20warm%20wooden%20desk%20with%20travel%20photos%20and%20flowers%2C%20soft%20natural%20light%2C%20creative%20team%20atmosphere%2C%20elegant%20cozy%20workspace&width=800&height=600&seq=about-brand-story&orientation=portrait"
                    alt="爱情岛团队"
                    className="w-full h-full object-cover object-top rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 text-center mb-3">
                我们的价值观
              </h2>
              <p className="text-foreground-500 text-sm text-center mb-10 md:mb-14 max-w-xl mx-auto">
                四条核心价值观，指引我们为情侣创造更好的旅行内容
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {brandValues.map((v) => (
                  <div key={v.title} className="bg-background-50 rounded-lg p-6 md:p-7 text-center">
                    <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
                      <i className={`${v.icon} text-xl`}></i>
                    </span>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-foreground-500 text-xs md:text-sm leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                  我们的成绩
                </h2>
                <p className="text-foreground-500 text-sm">用数字说话，见证爱情岛的成长</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { num: siteInfo.stats.articles.toLocaleString() + '+', label: '原创攻略' },
                  { num: siteInfo.stats.destinations + '+', label: '覆盖目的地' },
                  { num: (siteInfo.stats.couples / 10000).toFixed(0) + '万+', label: '服务情侣' },
                  { num: siteInfo.stats.satisfaction + '%', label: '用户满意度' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl md:text-4xl font-heading font-semibold text-primary-500 mb-1">{stat.num}</div>
                    <div className="text-sm text-foreground-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
                    联系我们
                  </h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </span>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</div>
                        <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                          {siteInfo.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </span>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-1">客服电话</div>
                        <div className="text-sm text-foreground-500">{siteInfo.phone}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </span>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-1">公司地址</div>
                        <div className="text-sm text-foreground-500">{siteInfo.address}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-5">
                    给我们留言
                  </h3>
                  <form onSubmit={handleContactSubmit} data-readdy-form="" className="space-y-4">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                      className="honeypot-field"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="name"
                        placeholder="你的名字"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="你的邮箱"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <input
                      type="text"
                      name="subject"
                      placeholder="主题"
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors"
                    />
                    <textarea
                      name="message"
                      placeholder="你的留言内容..."
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-foreground-800 text-sm outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送留言'}
                    </button>
                    {formStatus !== 'idle' && (
                      <p className={`text-sm text-center ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                        {formMsg}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <details key={i} className="bg-background-100 rounded-lg group">
                    <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-foreground-800 font-medium text-sm md:text-base">
                      {faq.question}
                      <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                    </summary>
                    <div className="px-6 pb-4 text-foreground-500 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}